<template>
  <div v-if="data.length > 0">
    <v-fade-transition>
      <v-btn
        v-if="is_chart_modified"
        class="reset-button"
        color="vtblue"
        large
        dark
        fixed
        bottom
        right
        @click="resetChartZoom"
      >
        {{ $t("common.labels.resetView") }}
        <v-icon right dark> mdi-restart </v-icon>
      </v-btn>
    </v-fade-transition>
    <div class="chart-container" v-for="(chart, index) in data" :key="`${index}_container`">
      <div :class="{ 'hide-container': !chart || chart.length == 0 }">
        <div class="mt-6 mb-4 primaryOutline--text text-subtitle-2">
          {{ config[index].label }}
        </div>
        <div :id="`${config[index].id}_2`" class="fill"></div>
        <div class="legends">
          <v-row no-gutters v-if="chart && lineSeries">
            <v-col
              cols="2"
              v-for="(d, idx) in chart"
              :key="`${config[index].id}_${idx}_legends`"
              :id="`${config[index].id}_${idx}_legends`"
            >
              <div
                class="d-flex legend text-caption"
                :style="{ color: config[index].colors[idx] }"
                @click="legendClicked($event, index, idx)"
              >
                <div :style="{ background: config[index].colors[idx] }" class="legend-color mr-1"></div>
                <div>
                  {{ d.label }}
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>
  </div>
  <div v-else>No data available</div>
</template>

<script>
import { LightningChart } from "../services/lightningchart";
import { content } from "../../config/general.js";
import moment from "moment";

import {
  AxisTickStrategies,
  synchronizeAxisIntervals,
  UIElementBuilders,
  SolidFill,
  SolidLine,
  ColorHEX,
  DashedLine,
  StipplePatterns,
} from "@arction/lcjs";
import { makeCustomTheme } from "@arction/lcjs-themes";

export default {
  name: "LightningChart",
  props: {
    data: Array,
    new_data: Array,
    config: Array,
    in_sync: Boolean,
    duration: String,
    alert_limit: Number,
    average_threshold: Object,
    custom_period: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    in_sync: function (newVal) {
      this.synchronizeCharts(newVal);
    },
    "$vuetify.theme.dark": {
      handler() {
        this.chart.map((c) => c.dispose());
        this.chart = [];
        this.axisX = [];
        this.initialize();
      },
    },
    data: function () {
      this.chart.map((c) => c.dispose());
      this.chart = [];
      this.axisX = [];
      this.period = {
        end_date: null,
        start_date: null,
      };
      this.initialize();
    },
    alert_limit: function (val) {
      // If alert limit is available, add a threshold line to the chart
      if (val) {
        const axisY = this.chart[0].getDefaultAxisY();
        if (val) {
          this.addThresholdLine(axisY, val, this.general_config.chart_threshold_colors.alert);
        }
      }
    },
    new_data: function (val) {
      // If alert limit is available, add a threshold line to the chart
      if (val && val.length > 0) {
        this.axisX.forEach((axis, index) => {
          val[index].map((data, idx) => {
            if (data.type === "bar") {
              this.lineSeries[index][idx][0].appendJSON(data.values);
            } else {
              this.lineSeries[index][idx][0].add(data.values);
            }
            // get minimum and maximum values of the chart
            let bounds = this.lineSeries[index][idx][0].getBoundaries();
            this.setBoundries(bounds);
          });
        });
      }
    },
  },
  data: function () {
    return {
      general_config: null,
      chart: [],
      lc: null,
      axisX: [],
      chartId: "test-lightning",
      syncHandle: null,
      lineSeries: [],
      barSeries: [],
      tickX: [],
      ticksY: [],
      textBox: [],
      is_chart_modified: false,
      interval: null,
      computed_data: null,
      timeout: null,
      period: {
        end_date: Number.NEGATIVE_INFINITY,
        start_date: Number.POSITIVE_INFINITY,
      },
    };
  },
  methods: {
    resetChartZoom() {
      // this function should reset the zoom and pan of the chart and revert to the original view
      this.chart.map((c) => {
        c.getDefaultAxisX().fit(false);
        c.getDefaultAxisY().fit(false);
        this.time_range &&
          c.getDefaultAxisX().setInterval({
            start: this.time_range[1],
            end: this.time_range[0],
          });
      });
      this.is_chart_modified = false;
    },
    initialize: function () {
      const self = this;
      const myCustomTheme = makeCustomTheme({
        isDark: this.$vuetify.theme.dark,
        fontFamily: "Roboto, sans-serif",
        backgroundColor: this.$vuetify.theme.dark ? ColorHEX("#1c1d21") : ColorHEX("#ffffff"),
        textColor: this.$vuetify.theme.dark ? ColorHEX("#ffffffaa") : ColorHEX("1c1d2188"),
        dataColors: [ColorHEX("#ffff5b"), ColorHEX("#ffcd5b"), ColorHEX("#ff9b5b")],
        axisColor: this.$vuetify.theme.dark ? ColorHEX("#00000000") : ColorHEX("1c1d2188"),
        gridLineColor: this.$vuetify.theme.dark ? ColorHEX("#303030ff") : ColorHEX("1c1d2122"),
        uiBackgroundColor: this.$vuetify.theme.dark ? ColorHEX("#303030ff") : ColorHEX("ffffff"),
        uiBorderColor: this.$vuetify.theme.dark ? ColorHEX("#303030ff") : ColorHEX("ffffff"),
        dashboardSplitterColor: this.$vuetify.theme.dark ? ColorHEX("#552d2d66") : ColorHEX("ffffff"),
        gradients: false,
      });
      this.lc = LightningChart.initialize();
      // const dateOrigin = new Date()
      this.data &&
        this.data.map((d, index) => {
          this.lineSeries[index] = [];
          let chart = this.lc.ChartXY({
            overrideInteractionMouseButtons: {
              chartXYPanMouseButton: 0, // LMB
              chartXYRectangleZoomFitMouseButton: 2, // RMB
            },
            container: `${this.config[index].id}_2`,
            theme: myCustomTheme,
            defaultAxisX: {
              type: "linear-highPrecision",
            },
            // defaultAxisXTickStrategy: AxisTickStrategies.DateTime
            // defaultAxisXTickStrategy: AxisTickStrategies.DateTime(dateOrigin)
            // , (tickStrategy) => tickStrategy.setDateOrigin(dateOrigin)
          });
          let axisX = chart
            .getDefaultAxisX()
            .setTitle(this.config[index].xAxis)
            .setTitleFont((font) => font.setSize(12));
          !this.config[index].noDateTimeXaxis
            ? axisX
                .setTickStrategy(AxisTickStrategies.DateTime, (tickStrategy) =>
                  tickStrategy.setMajorTickStyle((tickStyle) => tickStyle.setLabelFont((font) => font.setSize(12)))
                )
                .setInterval({
                  start: this.time_range[1],
                  end: this.time_range[0],
                })
            : axisX.setTickStrategy(AxisTickStrategies.Empty);

          chart
            .getDefaultAxisY()
            .setTitle(this.config[index].yAxis)
            .setTitleFont((font) => font.setSize(12))
            .setChartInteractionPanByDrag(false);

          const axisYLine =
            this.config[index].yLineAxis || this.config[index].yBarAxis
              ? chart
                  .addAxisY({ opposite: true })
                  .setTitle(this.config[index].yLineAxis || this.config[index].yBarAxis)
                  .setTitleFont((font) => font.setSize(12))
                  .setChartInteractionPanByDrag(false)
              : null;

          // Set chart title
          chart.setTitle("");

          chart.setMouseInteractionWheelZoom(false);
          axisX
            // Configure scrolling time axis
            // .setScrollStrategy(AxisScrollStrategies.fitting)
            // Disable default interactions on X axis
            .setAxisInteractionZoomByWheeling(false)
            .setAxisInteractionPanByDragging(false)
            .setNibInteractionScaleByWheeling(false)
            .setNibInteractionScaleByDragging(false);

          const handleMouseWheel = (_, event) => {
            this.is_chart_modified = true;
            // NOTE: This is a convenience method. Alternatively you can use `Axis.setInterval` for full control.
            !this.config[index].noDateTimeXaxis && axisX.zoom(axisX.getInterval().end, Math.sign(event.deltaY));
            if (
              this.interval &&
              (this.interval[0] > axisX.getInterval().start || this.interval[1] != axisX.getInterval().end)
            ) {
              // console.log("handleMouseWheel", this.interval);
              // this.interval[0] = axisX.getInterval().start;
              this.$emit("update-interval", ["start", new Date(this.interval[1])]);
            } else if (this.interval && this.interval[1] < axisX.getInterval().end) {
              // this.interval[1] = axisX.getInterval().end;
              this.$emit("update-interval", ["end", new Date(this.interval[0])]);
            }
            // console.log("handleMouseWheel", new Date(axisX.getInterval().start), new Date(axisX.getInterval().end));
            event.preventDefault();
            event.stopPropagation();
          };
          d &&
            d.map((data, idx) => {
              if (data.type === "bar") {
                const barSeries = chart.addPointLineAreaSeries({
                  // Optimize line series for progressively increasing X coordinates.
                  // dataPattern: { pattern: 'ProgressiveX', regularProgressiveStep: true, allowDataGrouping: true },
                  xAxis: chart.getDefaultAxisX(),
                  yAxis: idx != 0 ? axisYLine : chart.getDefaultAxisY(),
                  dataPattern: "ProgressiveX",
                });
                // .setDefaultStyle((figure) =>
                //   figure.setStrokeStyle((stroke) =>
                //     stroke
                //       .setThickness(1)
                //       .setFillStyle(new SolidFill({ color: ColorHEX(this.config[index].colors[idx]) }))
                //   )
                // );
                // data.values.forEach((point) => {
                //   barSeries.add({ startX: point.x, startY: 0, endX: point.x, endY: point.y });
                // });
                // console.log("***", barSeries.getAreaFillStyle());
                barSeries
                  .setCurvePreprocessing({ type: "step", step: "middle" })
                  .appendJSON(data.values)
                  .setPointFillStyle(new SolidFill({ color: ColorHEX(this.config[index].colors[idx]) }).setA(100))
                  .setAreaFillStyle(new SolidFill({ color: ColorHEX(this.config[index].colors[idx]) }).setA(100))
                  .setPointSize(1)
                  .setStrokeStyle(
                    new SolidLine({
                      thickness: 2,
                      fillStyle: new SolidFill({ color: ColorHEX(this.config[index].colors[idx]) }).setA(100),
                    })
                  )
                  .setName(data.label || data.id)
                  .onMouseWheel(handleMouseWheel);
                barSeries.setCursorEnabled(false);
                this.lineSeries[index][idx] = [barSeries, true, "bar"];
              } else {
                const lineSeries = chart.addLineSeries({
                  // Optimize line series for progressively increasing X coordinates.
                  // dataPattern: { pattern: 'ProgressiveX', regularProgressiveStep: true, allowDataGrouping: true },
                  xAxis: chart.getDefaultAxisX(),
                  yAxis: chart.getDefaultAxisY(),
                });
                // Add data points to the line series
                lineSeries
                  .add(data.values)
                  .setStrokeStyle(
                    new SolidLine({
                      thickness: 1,
                      fillStyle: new SolidFill({
                        color: ColorHEX(this.config[index].colors[idx]),
                      }),
                    })
                  )
                  .setName(data.label || data.id)
                  .onMouseWheel(handleMouseWheel);
                lineSeries.setCursorEnabled(false);
                //.setStrokeStyle((style) => style.setThickness(5));
                this.lineSeries[index][idx] = [lineSeries, true, "line"];
              }
              let bounds = this.lineSeries[index][idx][0].getBoundaries();
              this.interval = [bounds.min.x, bounds.max.x];
              /**lineSeries = [
               *   [
               *    ["lineSeies1", isVisible],
               *    ["lineSeies1", isVisible],
               *    ["lineSeies1", isVisible]
               *   ],
               *   ["chart_2 lineseries array"],
               *   ["chart_3 lineseries array"]
               *  ]
               * each element in lineSeries denotes chart_id
               * each element inside  is a lineSeries in that chart_id**/

              // Add threshold line
              const axisY = chart.getDefaultAxisY();
              /*
            # Multiple threshold lines can be added to the same axis.
            # To do so, simply call addThresholdLine multiple times,
            # with different parameters (nom, min, max)
          */
              this.config[index].nom
                ? this.addThresholdLine(axisY, this.config[index].nom, this.general_config.chart_threshold_colors.Nom)
                : null;

              this.config[index].min
                ? this.addThresholdLine(axisY, this.config[index].min, this.general_config.chart_threshold_colors.Min)
                : null;
              this.config[index].max
                ? this.addThresholdLine(axisY, this.config[index].max, this.general_config.chart_threshold_colors.Max)
                : null;
              this.config[index].alert_limit && this.alert_limit
                ? this.addThresholdLine(axisY, this.alert_limit, this.general_config.chart_threshold_colors.Alert)
                : null;
              this.config[index].average_threshold &&
              this.average_threshold &&
              this.average_threshold[this.config[index].id].avg
                ? this.addBoundries(axisY, this.average_threshold[this.config[index].id].avg)
                : null;
            });
          // Set stroke style of the line
          // lineSeries.setStrokeStyle((style) => style.setThickness(5))
          // Trigger custom event handler when mouse wheeling over chart, X axis or even series.
          chart.onSeriesBackgroundMouseWheel(handleMouseWheel);
          // chart.onSeriesBackgroundMouseDragStart(handleMouseWheel);
          chart.onSeriesBackgroundMouseDragStart(() => {
            this.is_chart_modified = true;
            // console.log(
            //   "onSeriesBackgroundMouseDragStart",
            //   new Date(axisX.getInterval().start),
            //   new Date(axisX.getInterval().end)
            // );
          });
          axisX.onAxisInteractionAreaMouseWheel(handleMouseWheel);
          this.tickX[index] = chart
            .getDefaultAxisX()
            .addCustomTick(UIElementBuilders.PointableTextBox)
            .setAllocatesAxisSpace(false);
          this.ticksY[index] = this.lineSeries[index].map(() => {
            return chart
              .getDefaultAxisY()
              .addCustomTick(UIElementBuilders.PointableTextBox)
              .setAllocatesAxisSpace(false);
            // .setMarker((marker) => marker.setTextFillStyle(el[0].getStrokeStyle().getFillStyle()));
          });
          this.tickX[index].setVisible(false);
          this.ticksY[index].forEach((tick) => tick.setVisible(false));
          this.chart.push(chart);
          // Add Synchronus Customised tooltip for each chart
          this.setSynchronousHover(chart, index, self);
          this.axisX.push(axisX);
          // get minimum and maximum values of the chart
          //console.log(this.lineSeries[index] && this.lineSeries[index].length > 0);
          let bounds =
            this.lineSeries[index] &&
            this.lineSeries[index].length > 0 &&
            !this.custom_period &&
            this.lineSeries[index][0][0].getBoundaries();
          this.setBoundries(bounds);
        });

      this.syncHandle = this.synchronizeCharts(true);
      this.resetChartZoom();
    },
    setBoundries(bounds) {
      if (!this.isBoundValid(bounds)) return;
      this.period.start_date = moment(bounds.max.x).valueOf();
      this.period.end_date = moment(bounds.min.x).valueOf();
    },
    isBoundValid(bounds) {
      // 1. Check if the value is actually a date (Not empty string, not zero or some other invalid value)
      // 2. Check if the value is within the range of the data
      return bounds && bounds.min.x && bounds.max.x && bounds.min.x < bounds.max.x;
    },
    addThresholdLine(axisY, configValue, colorConfig, isSolid) {
      let lineStyle = isSolid
        ? new SolidLine({
            thickness: 0.1,
            fillStyle: new SolidFill({
              color: ColorHEX(colorConfig),
            }),
          })
        : new DashedLine({
            thickness: 0.1,
            fillStyle: new SolidFill({
              color: ColorHEX(colorConfig),
            }),
            pattern: StipplePatterns.Dashed,
            patternScale: 4,
          });

      return axisY.addConstantLine(true).setValue(configValue).setStrokeStyle(lineStyle).setMouseInteractions(false);
    },
    addBoundries(axis, avg) {
      let values = { lower: avg * 0.8, upper: avg * 1.2 };
      this.addThresholdLine(axis, values.lower, this.general_config.chart_threshold_colors.LowerAvg, true);
      this.addThresholdLine(axis, values.upper, this.general_config.chart_threshold_colors.UpperAvg, true);
    },
    legendClicked: function (event, chart_id, line_id) {
      var target_class = event.target.classList.contains("legend");
      target_class ? event.target.classList.toggle("disable") : event.target.parentElement.classList.toggle("disable");
      this.lineSeries.forEach((e, i) => {
        if (i == chart_id) {
          e.forEach((e1, i1) => {
            if (i1 == line_id) {
              e1[1] = !e1[1]; //Update series visibility param
              e1[0].setVisible(e1[1]);
            }
          });
        }
      });
    },
    synchronizeCharts: function (enable) {
      if (enable) return synchronizeAxisIntervals.apply(null, this.axisX);
      else this.syncHandle.remove();
      return null;
    },
    setSynchronousHover(chart, index, self) {
      self.chart.forEach((el, indx) => {
        // Create custom cursor UI by adding HTML elements to the document.
        document.head.appendChild(document.createElement("style"));
        const textBox = document.createElement("div");
        textBox.id = "resultTable";
        const line = document.createElement("div");
        line.id = "line";
        const line2 = document.createElement("div");
        line2.id = "line2";
        const arrow = document.createElement("div");
        arrow.id = "arrow";
        textBox.appendChild(line);
        textBox.appendChild(line2);
        textBox.appendChild(arrow);
        el.engine.container.append(textBox);
        el.setAnimationsEnabled(false);
        self.textBox[indx] = { textBox, line, line2, arrow };
      });
      // Implement custom cursor logic with events.
      const showTooltip = (clientCoordinate) => {
        self.chart.forEach((el, indx) => {
          const { nearestDataPoints, nearestPoint } = getNearestPoint(el, indx, clientCoordinate);
          if (nearestPoint && nearestPoint.location && nearestPoint.location.x) {
            // Translate data point from Axis to client.
            const nearestPointClient = el.translateCoordinate(nearestPoint.location, el.coordsAxis, el.coordsClient);

            // Position and format custom HTML cursor.
            if (nearestPoint.location.x > el.getDefaultAxisX().getInterval().end / 1.5) {
              self.textBox[indx].textBox.style.left = `${Math.round(
                nearestPointClient.clientX - self.textBox[indx].textBox.clientWidth - 10
              )}px`;
              self.textBox[indx].textBox.style.top = `${Math.round(
                nearestPointClient.clientY - self.textBox[indx].textBox.clientHeight / 2
              )}px`;
              self.textBox[indx].arrow.style.left = "165px";
              self.textBox[indx].arrow.style.transform = "translate(-40%, -50%) rotate(-135deg)";
            } else {
              self.textBox[indx].textBox.style.left = `${Math.round(nearestPointClient.clientX + 10)}px`;
              self.textBox[indx].textBox.style.top = `${Math.round(
                nearestPointClient.clientY - self.textBox[indx].textBox.clientHeight / 2
              )}px`;
              self.textBox[indx].arrow.style.left = "-1px";
              self.textBox[indx].arrow.style.transform = "translate(-50%, -50%) rotate(45deg)";
            }

            const point = nearestDataPoints.map((el) => (el ? el.location.y : 0));
            let rowElement = "";
            for (let i = 0; i < self.lineSeries[indx].length; i++) {
              self.textBox[indx].line.innerHTML = `<b>${
                nearestDataPoints[i]
                  ? !this.config[index].noDateTimeXaxis
                    ? el.getDefaultAxisX().formatValue(nearestDataPoints[i].location.x)
                    : new Date(nearestDataPoints[i].location.x).toLocaleString("en-GB", {
                        hour12: false,
                      }) +
                      ":" +
                      new Date(nearestDataPoints[i].location.x).getMilliseconds()
                  : ""
              }
              </b>`;
              const sign = point[i] > 0 ? "+" : "";
              rowElement += `<div class='values' style='color: ${
                self.config[indx].colors[i]
              }' ><span class="borderLight--text">${self.lineSeries[indx][i][0].getName()}:</span> <b>${
                nearestDataPoints[i] ? sign + el.getDefaultAxisY().formatValue(nearestDataPoints[i].location.y) : ""
              }  ${self.data[indx][i].suffix ? self.data[indx][i].suffix : ""}</b>
                </div>`;
            }
            self.textBox[indx].line2.innerHTML = rowElement;
            self.textBox[indx].textBox.style.opacity = 1;
          } else {
            self.textBox[indx].textBox.style.opacity = 0;
          }
        });
      };

      const showCursorAt = (clientCoordinate) => {
        self.chart.forEach((el, indx) => {
          // Find the nearest solved data point to the mouse.
          const { nearestPoint } = getNearestPoint(el, indx, clientCoordinate);

          if (nearestPoint && nearestPoint.location && nearestPoint.location.x) {
            // Position custom ticks.
            self.tickX.forEach((e) => e.setValue(nearestPoint.location.x));
            self.tickX.forEach((e) => e.setVisible(true));
          } else {
            self.tickX.forEach((e) => e.setVisible(false));
          }
        });
      };

      const mouseMoveHandler = (_, event) => {
        self.textBox.map((e) => (e.textBox.style.opacity = 0));
        self.tickX.map((e) => e.setVisible(false));
        self.timeout && clearTimeout(self.timeout);
        self.timeout = setTimeout(function () {
          showTooltip(event);
        }, 100);
        showCursorAt(event);
      };

      const getNearestPoint = (el, indx, clientCoordinate) => {
        // Translate mouse location to Axis.
        const mouseLocationAxis = el.translateCoordinate(clientCoordinate, el.coordsAxis);

        // Solve nearest data point to the mouse on each series.
        const nearestDataPoints = self.lineSeries[indx].map((elm, i) => {
          return elm[0].solveNearestFromScreen({
            clientX: clientCoordinate.clientX,
            clientY: clientCoordinate.clientY,
          });
        });
        // Find the nearest solved data point to the mouse.
        const nearestPoint =
          nearestDataPoints && nearestDataPoints.length > 0
            ? nearestDataPoints.reduce((prev, curr) => {
                if (!prev) return curr;
                if (!curr) return prev;
                return Math.abs(mouseLocationAxis.y - curr.location.y) < Math.abs(mouseLocationAxis.y - prev.location.y)
                  ? curr
                  : prev;
              })
            : null;
        return { nearestDataPoints, nearestPoint };
      };
      self.lineSeries.map((e) => {
        e.forEach((series) => {
          series[0].onMouseMove(mouseMoveHandler);
        });
      });
      chart.onSeriesBackgroundMouseMove(mouseMoveHandler);
      chart.onSeriesBackgroundMouseLeave(() => {
        self.timeout && clearTimeout(self.timeout);
        self.textBox.map((e) => (e.textBox.style.opacity = 0));
        self.tickX.map((e) => e.setVisible(false));
        // self.ticksY.map((e) => e.forEach((tick) => tick.setVisible(false)));
      });
      chart.onSeriesBackgroundMouseDragStart(() => {
        self.textBox.map((e) => (e.textBox.style.opacity = 0));
        self.tickX.map((e) => e.setVisible(false));
        // self.ticksY.map((e) => e.forEach((tick) => tick.setVisible(false)));
      });

      chart.getDefaultAxisY().onIntervalChange(() => {
        self.textBox.map((e) => (e.textBox.style.opacity = 0));
        self.tickX.map((e) => e.setVisible(false));
      });
      chart.getDefaultAxisX().onIntervalChange(() => {
        self.textBox.map((e) => (e.textBox.style.opacity = 0));
        self.tickX.map((e) => e.setVisible(false));
      });

      // Add CSS.
      function addStyle(styleString) {
        const style = document.createElement("style");
        style.textContent = styleString;
        document.head.append(style);
      }

      addStyle(`
    #resultTable {
        background-color: var(--v-backgroundWhite-base);
        color: var(--v-primary-base);
        font-size: 11px;
        border: solid var(--v-borderLight-base) 1px;
        border-radius: 5px;
        width: 165px;
        // height: 110px;
        height: auto;
        top: 0;
        left: 0;
        position: fixed;
        padding: 0;
        pointer-events:none;
        z-index: 1;
        transition: opacity 0.05s;
        opacity: 0.0;
    }
    #arrow {
        position: absolute;
        background-color: var(--v-backgroundWhite-base);
        height: 10px;
        width: 10px;
        top: 49%;
        border-left: solid var(--v-borderLight-base) 1px;
        border-bottom: solid var(--v-borderLight-base) 1px;
        box-shadow: 0 0 10px rgba(0, 0, 0, .5);
        transform-origin: center center;
    }
    p {
        margin:0;
    }
    #date {
        text-align: center;
    }
    .values {
        display: flex;
        justify-content: space-between;
        align-items center;
        margin: 5px 0;
    }
    .values span {
        margin-right: 10px
        font-size: 10px;
    }
    #line {
        background-color: var(--v-backgroundDark-base);
        border-top-right-radius: 4px;
        border-top-left-radius: 4px;
        margin: 0;
        padding: 7px;
        font-size: 11px;
        height: 20%;
        font-family: Roboto, sans-serif;
        width: auto;
        heigth: 100%;
    }
    #line2 {
        font-size: 11px;
        font-weight: 900;
        padding: 2px 10px
    }
`);
    },
  },
  created() {
    this.general_config = content;
  },
  mounted() {
    this.initialize();
  },
  computed: {
    time_range: function () {
      const today = new Date().getTime();
      return this.period && !this.custom_period
        ? [this.period.start_date, this.period.end_date]
        : [today, today - parseInt(this.duration) * 86400000];
    },
  },
  beforeDestroy() {
    // "dispose" should be called when the component is unmounted to free all the resources used by the chart
    this.chart.map((chart) => chart.dispose());
  },
};
</script>
<style lang="scss" scoped>
.chart-container {
  margin: 0 1rem;
}

.fill {
  height: 280px;
  margin: 0 1rem;
}

.legends {
  margin: 0 1rem 2rem;

  .legend {
    cursor: pointer;

    .legend-color {
      border-radius: 50%;
      width: 16px;
      height: 16px;
    }
  }

  .legend.disable {
    color: grey !important;

    .legend-color {
      background-color: grey !important;
    }
  }
}

.reset-button {
  z-index: 100;
}

.hide-container {
  display: none;
}
</style>
